/**
 * Live-call detection — kept in sync with mobile ragVoiceApi.ts
 * (isLiveCallLog, listLiveCalls, HomeScreen liveFeedEntries merge).
 */

const LIVE_STATUSES = new Set([
  'live',
  'ringing',
  'in_progress',
  'initiated',
  'queued',
  'active',
]);

const ACTIVE_OUTCOMES = new Set(['initiated', 'pending', 'ringing', 'in_progress', 'live']);

const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'ended',
  'cancelled',
  'canceled',
  'no_answer',
  'busy',
]);

const SCHEDULED_FIRING_STATUSES = new Set([
  'queued',
  'ringing',
  'in_progress',
  'live',
  'active',
]);

/** Mirrors backend _MAX_LIVE_AGE_SECONDS (4h) — stale rows are not live. */
const MAX_LIVE_AGE_MS = 4 * 60 * 60 * 1000;

function normalizeKey(value) {
  return (value || '').toLowerCase().replace(/-/g, '_');
}

function resolveOutboundKind(log) {
  return (log?.outbound_kind || log?.source || '').toLowerCase();
}

function isPendingScheduledCall(log) {
  if (resolveOutboundKind(log) !== 'scheduled') return false;
  const status = normalizeKey(log?.status);
  if (TERMINAL_STATUSES.has(status)) return false;
  if (SCHEDULED_FIRING_STATUSES.has(status)) return false;
  if (status === 'scheduled' || status === 'initiated') return true;
  if (log?.scheduled_at && !log?.connected_at && !log?.ended_at) {
    const scheduledMs = new Date(log.scheduled_at).getTime();
    if (Number.isFinite(scheduledMs) && scheduledMs > Date.now() - 30_000) {
      return true;
    }
  }
  return false;
}

function callStartedAtMs(log) {
  for (const key of ['connected_at', 'started_at', 'created_at']) {
    const raw = log?.[key];
    if (!raw) continue;
    const ms = new Date(raw).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

/** Mirrors backend _is_within_live_age. */
function isWithinLiveAge(log, maxAgeMs = MAX_LIVE_AGE_MS) {
  const started = callStartedAtMs(log);
  if (started == null) return true;
  return Date.now() - started <= maxAgeMs;
}

/** Mirrors backend _is_live_call_log (including max age and ended_at). */
export function isLiveCallLog(log) {
  if (!log) return false;
  if (log.ended_at) return false;
  if (isPendingScheduledCall(log)) return false;

  const status = normalizeKey(log.status);
  if (status === 'completed' || status === 'failed' || status === 'ended') return false;

  const outcome = normalizeKey(log.outcome);
  if (outcome && !ACTIVE_OUTCOMES.has(outcome)) return false;

  if (!LIVE_STATUSES.has(status) && !ACTIVE_OUTCOMES.has(outcome)) return false;
  return isWithinLiveAge(log);
}

/** Mirrors mobile sessionToCallLog. */
export function sessionToCallLog(session) {
  const cl = session?.call_log || {};
  return {
    ...cl,
    id: session?.call_log_id || cl.id || '',
    call_type: cl.call_type || cl.direction || 'inbound',
    customer_phone: cl.customer_phone || session?.customer_phone || '',
    customer_name: cl.customer_name || cl.contact_name || '',
    uuid: session?.business_id || cl.uuid,
    business_name: cl.business_name,
    status: cl.status || session?.status,
    outcome: cl.outcome,
    outbound_kind: cl.outbound_kind,
    source: cl.source,
    connected_at: cl.connected_at,
    created_at: cl.created_at,
    ended_at: cl.ended_at,
    vapi_call_id: cl.vapi_call_id || session?.vapi_call_id,
    transcript_lines: cl.transcript_lines,
    summary: cl.summary || cl.call_summary,
    chat_summary: cl.chat_summary,
  };
}

/**
 * Mirrors mobile listLiveCalls() for /voice/calls/live-inbound rows.
 * Backend already filters stale rows; avoid double-filtering that hid live calls on web.
 */
export function normalizeLiveSessions(apiSessions) {
  return (apiSessions || [])
    .filter(s => normalizeKey(s?.status) === 'live' && (s.call_log_id || s.call_log?.id))
    .map(session => {
      const log = sessionToCallLog(session);
      if (isPendingScheduledCall(log)) return null;
      if (!isLiveCallLog(log)) return null;
      return {
        ...session,
        call_log_id: session.call_log_id || log.id,
        status: 'live',
        customer_phone: session.customer_phone || log.customer_phone,
        created_at:
          session.created_at || log.connected_at || log.created_at || new Date().toISOString(),
        call_log: { ...log, ...(session.call_log || {}) },
        vapi_call_id: session.vapi_call_id || log.vapi_call_id || '',
      };
    })
    .filter(Boolean);
}

export function callLogToLiveSession(log, businessId) {
  return {
    call_log_id: log.id,
    business_id: businessId,
    customer_phone: log.customer_phone || '',
    created_at: log.connected_at || log.created_at || new Date().toISOString(),
    status: 'live',
    call_log: log,
    vapi_call_id: log.vapi_call_id || '',
  };
}

/**
 * Merge live-inbound API rows with recent call_logs — same as mobile Home liveFeedEntries.
 */
export function mergeLiveSessions(apiSessions, callLogs, businessId) {
  const byId = new Map();

  for (const session of normalizeLiveSessions(apiSessions)) {
    const id = session?.call_log_id;
    if (id) byId.set(id, session);
  }

  for (const log of callLogs || []) {
    if (log.deleted || log.archived) continue;
    if (!isLiveCallLog(log)) continue;
    const id = log.id;
    if (!id || byId.has(id)) continue;
    byId.set(id, callLogToLiveSession(log, businessId));
  }

  return Array.from(byId.values()).sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  });
}
